import React, { Fragment } from "react";
import type { NamedSeries } from "./LineChart";
import type { GroupedColumn } from "../chartUtils";
import dayjs from "dayjs";

// Alternating column colors for better readability
const getColumnBgColor = (index: number): string => {
  const colors = ["#f8f9fa", "#e9ecef"];
  return colors[index % colors.length];
};

interface DataTableProps {
  /** simple mode: one value column per series (vibration / distortion) */
  series?: NamedSeries[];
  unit?: string;
  numberFormat?: (v: number) => string;

  /** grouped mode: R.L + Diff per device (settlement / ground water / crack / tilting) */
  grouped?: GroupedColumn[];
  readingUnit?: string;
  changeUnit?: string;
  readingFormat?: (v: number) => string;
  changeFormat?: (v: number) => string;
  preExistingFormat?: (v: number) => string;
  /** Use crack gauge layout (X/Y paired per device) */
  isCrackGauge?: boolean;
}

const defaultFormat = (v: number) => v.toFixed(3);

function downloadCsv(header: string[], rows: string[][]) {
  const csv = [header, ...rows]
    .map((r) => r.map((c) => `"${c}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `data-table-${dayjs().format("YYYY-MM-DD")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const EmptyMsg: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-muted" style={{ padding: 40, textAlign: "center" }}>
    {text}
  </div>
);

// ---------- grouped layout (matches front SettlementDataTable) ----------
const GroupedTable: React.FC<{
  grouped: GroupedColumn[];
  readingUnit: string;
  changeUnit: string;
  readingFormat: (v: number) => string;
  changeFormat: (v: number) => string;
  preExistingFormat: (v: number) => string;
}> = ({
  grouped,
  readingUnit,
  changeUnit,
  readingFormat,
  changeFormat,
  preExistingFormat,
}) => {
  const isTiltingTable = readingUnit === "1:N" && changeUnit === "1:N";
  const readingLabel = isTiltingTable
    ? `Reading (${readingUnit})`
    : `R.L (${readingUnit})`;
  const isSplitReadingColumn = (column: GroupedColumn) =>
    column.isHorizontalMovement === true;

  const dateSet = new Set<string>();
  grouped.forEach((c) => {
    c.readingByDay.forEach((_, d) => dateSet.add(d));
    c.changeByDay.forEach((_, d) => dateSet.add(d));
  });
  const dates = Array.from(dateSet).sort().reverse();

  const exportCsv = () => {
    const header = ["Point no."];
    grouped.forEach((c) => {
      if (isSplitReadingColumn(c)) {
        header.push(`${c.name} ${c.primaryReadingLabel ?? "Northing"}`);
        header.push(`${c.name} ${c.secondaryReadingLabel ?? "Easting"}`);
        header.push(`${c.name} Diff.(${changeUnit})`);
      } else {
        header.push(`${c.name} ${readingLabel}`);
        header.push(`${c.name} Diff.(${changeUnit})`);
      }
    });
    const rows = dates.map((d) => {
      const row = [d];
      grouped.forEach((c) => {
        const r = c.readingByDay.get(d);
        const secondary = c.secondaryReadingByDay?.get(d);
        const ch = c.changeByDay.get(d);
        if (isSplitReadingColumn(c)) {
          row.push(r != null ? readingFormat(r) : "");
          row.push(secondary != null ? readingFormat(secondary) : "");
          row.push(ch != null ? changeFormat(ch) : "");
        } else {
          row.push(r != null ? readingFormat(r) : "");
          row.push(ch != null ? changeFormat(ch) : "");
        }
      });
      return row;
    });
    downloadCsv(header, rows);
  };

  if (!grouped.length) return <EmptyMsg text="No devices selected." />;
  if (!dates.length)
    return <EmptyMsg text="No readings in the selected range." />;

  return (
    <>
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="text-muted text-sm">
          {dates.length} day{dates.length > 1 ? "s" : ""} · {grouped.length}{" "}
          column{grouped.length > 1 ? "s" : ""}
        </span>
        <button className="btn" onClick={exportCsv}>
          <i className="bi bi-download" /> Export CSV
        </button>
      </div>
      <div className="data-table-wrap">
        <table className="table grouped-data-table">
          <thead>
            <tr>
              <th rowSpan={4}>Point no.</th>
              {grouped.map((c) => (
                <th
                  key={`${c.name}-name`}
                  colSpan={isSplitReadingColumn(c) ? 3 : 2}
                  className="text-center"
                >
                  {c.name}
                </th>
              ))}
            </tr>
            <tr>
              {grouped.map((c) => {
                if (isSplitReadingColumn(c)) {
                  return (
                    <Fragment key={`${c.name}-init`}>
                      <th className="text-sm">Initial reading</th>
                      <th className="text-sm text-center">
                        {c.initialReading != null
                          ? readingFormat(c.initialReading)
                          : "—"}
                        {c.initialDate ? ` (${c.initialDate})` : ""}
                      </th>
                      <th className="text-sm text-center">
                        {c.secondaryInitialReading != null
                          ? readingFormat(c.secondaryInitialReading)
                          : "—"}
                      </th>
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={`${c.name}-init`}>
                    <th className="text-sm">Initial reading ({readingUnit})</th>
                    <th className="text-sm text-center">
                      {c.initialReading != null
                        ? readingFormat(c.initialReading)
                        : "—"}
                      {c.initialDate ? ` (${c.initialDate})` : ""}
                    </th>
                  </Fragment>
                );
              })}
            </tr>
            <tr>
              {grouped.map((c) => {
                if (isSplitReadingColumn(c)) {
                  return (
                    <Fragment key={`${c.name}-pe`}>
                      <th colSpan={2} className="text-sm">
                        Pre-existing ({changeUnit})
                      </th>
                      <th className="text-sm text-center">
                        {preExistingFormat(c.preExisting)}
                      </th>
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={`${c.name}-pe`}>
                    <th className="text-sm">Pre-existing ({changeUnit})</th>
                    <th className="text-sm text-center">
                      {preExistingFormat(c.preExisting)}
                    </th>
                  </Fragment>
                );
              })}
            </tr>
            <tr>
              {grouped.map((c) => {
                if (isSplitReadingColumn(c)) {
                  return (
                    <Fragment key={`${c.name}-sub`}>
                      <th className="text-center">
                        {c.primaryReadingLabel ?? "Northing"}
                      </th>
                      <th className="text-center">
                        {c.secondaryReadingLabel ?? "Easting"}
                      </th>
                      <th className="text-center">Diff.({changeUnit})</th>
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={`${c.name}-sub`}>
                    <th className="text-center">{readingLabel}</th>
                    <th className="text-center">Diff.({changeUnit})</th>
                  </Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {dates.map((d) => (
              <tr key={d}>
                <td className="fw-600" style={{ backgroundColor: "#e9ecef" }}>
                  {d}
                </td>
                {grouped.map((c, idx) => {
                  const r = c.readingByDay.get(d);
                  const secondary = c.secondaryReadingByDay?.get(d);
                  const ch = c.changeByDay.get(d);
                  const bgColor = getColumnBgColor(idx);

                  if (isSplitReadingColumn(c)) {
                    return (
                      <Fragment key={`${c.name}-${d}`}>
                        <td
                          className="text-center"
                          style={{ backgroundColor: bgColor }}
                        >
                          {r != null ? readingFormat(r) : "-"}
                        </td>
                        <td
                          className="text-center"
                          style={{ backgroundColor: bgColor }}
                        >
                          {secondary != null ? readingFormat(secondary) : "-"}
                        </td>
                        <td
                          className="text-center"
                          style={{ backgroundColor: bgColor }}
                        >
                          {ch != null ? changeFormat(ch) : "-"}
                        </td>
                      </Fragment>
                    );
                  }

                  return (
                    <Fragment key={`${c.name}-${d}`}>
                      <td
                        className="text-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        {r != null ? readingFormat(r) : "-"}
                      </td>
                      <td
                        className="text-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        {ch != null ? changeFormat(ch) : "-"}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ---------- crack gauge layout (X/Y paired per device) ----------
const CrackGaugeTable: React.FC<{
  grouped: GroupedColumn[];
  readingUnit: string;
  changeUnit: string;
  readingFormat: (v: number) => string;
  changeFormat: (v: number) => string;
  preExistingFormat: (v: number) => string;
}> = ({
  grouped,
  readingUnit,
  changeUnit,
  readingFormat,
  changeFormat,
  preExistingFormat,
}) => {
  // Pair X and Y columns for each device
  const devices: Array<{
    name: string;
    xCol: GroupedColumn | undefined;
    yCol: GroupedColumn | undefined;
  }> = [];
  const deviceMap = new Map<string, (typeof devices)[0]>();

  grouped.forEach((c) => {
    const match = c.name.match(/^(.+)\s+([XY])$/);
    if (match) {
      const baseName = match[1];
      const axis = match[2];
      let dev = deviceMap.get(baseName);
      if (!dev) {
        dev = { name: baseName, xCol: undefined, yCol: undefined };
        deviceMap.set(baseName, dev);
        devices.push(dev);
      }
      if (axis === "X") dev.xCol = c;
      else dev.yCol = c;
    }
  });

  const dateSet = new Set<string>();
  grouped.forEach((c) => {
    c.readingByDay.forEach((_, d) => dateSet.add(d));
    c.changeByDay.forEach((_, d) => dateSet.add(d));
  });
  const dates = Array.from(dateSet).sort().reverse();

  const exportCsv = () => {
    const header = ["Point no."];
    devices.forEach((dev) => {
      header.push(
        `${dev.name} X (${readingUnit})`,
        `${dev.name} Y (${readingUnit})`,
        `${dev.name} ΔX (${changeUnit})`,
        `${dev.name} ΔY (${changeUnit})`,
      );
    });
    const rows = dates.map((d) => {
      const row = [d];
      devices.forEach((dev) => {
        const xR = dev.xCol?.readingByDay.get(d);
        const yR = dev.yCol?.readingByDay.get(d);
        const xC = dev.xCol?.changeByDay.get(d);
        const yC = dev.yCol?.changeByDay.get(d);
        row.push(
          xR != null ? readingFormat(xR) : "",
          yR != null ? readingFormat(yR) : "",
          xC != null ? changeFormat(xC) : "",
          yC != null ? changeFormat(yC) : "",
        );
      });
      return row;
    });
    downloadCsv(header, rows);
  };

  if (!devices.length) return <EmptyMsg text="No devices selected." />;
  if (!dates.length)
    return <EmptyMsg text="No readings in the selected range." />;

  return (
    <>
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="text-muted text-sm">
          {dates.length} day{dates.length > 1 ? "s" : ""} · {devices.length}{" "}
          device{devices.length > 1 ? "s" : ""}
        </span>
        <button className="btn" onClick={exportCsv}>
          <i className="bi bi-download" /> Export CSV
        </button>
      </div>
      <div className="data-table-wrap">
        <table className="table grouped-data-table">
          <thead>
            <tr>
              <th rowSpan={4}>Point no.</th>
              {devices.map((dev) => (
                <th
                  key={`${dev.name}-name`}
                  colSpan={4}
                  className="text-center"
                >
                  {dev.name}
                </th>
              ))}
            </tr>
            <tr>
              {devices.map((dev) => (
                <Fragment key={`${dev.name}-init`}>
                  <th colSpan={2} className="text-sm text-center">
                    Initial reading ({readingUnit})
                  </th>
                  <th className="text-sm text-center">
                    {dev.xCol?.initialReading != null
                      ? readingFormat(dev.xCol.initialReading)
                      : "—"}
                  </th>
                  <th className="text-sm text-center">
                    {dev.yCol?.initialReading != null
                      ? readingFormat(dev.yCol.initialReading)
                      : "—"}
                  </th>
                </Fragment>
              ))}
            </tr>
            <tr>
              {devices.map((dev) => (
                <Fragment key={`${dev.name}-pe`}>
                  <th colSpan={2} className="text-sm text-center">
                    Pre-existing ({changeUnit})
                  </th>
                  <th className="text-sm text-center">
                    {dev.xCol ? preExistingFormat(dev.xCol.preExisting) : "—"}
                  </th>
                  <th className="text-sm text-center">
                    {dev.yCol ? preExistingFormat(dev.yCol.preExisting) : "—"}
                  </th>
                </Fragment>
              ))}
            </tr>
            <tr>
              {devices.map((dev) => (
                <Fragment key={`${dev.name}-sub`}>
                  <th className="text-center">X ({readingUnit})</th>
                  <th className="text-center">Y ({readingUnit})</th>
                  <th className="text-center">ΔX ({changeUnit})</th>
                  <th className="text-center">ΔY ({changeUnit})</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((d) => (
              <tr key={d}>
                <td className="fw-600" style={{ backgroundColor: "#e9ecef" }}>
                  {d}
                </td>
                {devices.map((dev, idx) => {
                  const xR = dev.xCol?.readingByDay.get(d);
                  const yR = dev.yCol?.readingByDay.get(d);
                  const xC = dev.xCol?.changeByDay.get(d);
                  const yC = dev.yCol?.changeByDay.get(d);
                  const bgColor = getColumnBgColor(idx);
                  return (
                    <Fragment key={`${dev.name}-${d}`}>
                      <td
                        className="text-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        {xR != null ? readingFormat(xR) : "-"}
                      </td>
                      <td
                        className="text-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        {yR != null ? readingFormat(yR) : "-"}
                      </td>
                      <td
                        className="text-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        {xC != null ? changeFormat(xC) : "-"}
                      </td>
                      <td
                        className="text-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        {yC != null ? changeFormat(yC) : "-"}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ---------- simple layout (vibration / distortion) ----------
const SimpleTable: React.FC<{
  series: NamedSeries[];
  unit: string;
  numberFormat: (v: number) => string;
}> = ({ series, unit, numberFormat }) => {
  const dateSet = new Set<string>();
  series.forEach((s) =>
    s.points.forEach((p) => dateSet.add(dayjs(p.date).format("YYYY-MM-DD"))),
  );
  const dates = Array.from(dateSet).sort().reverse();

  const bySeries = series.map((s) => {
    const m = new Map<string, number>();
    [...s.points]
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((p) => m.set(dayjs(p.date).format("YYYY-MM-DD"), p.value));
    return { name: s.name, m };
  });

  const exportCsv = () => {
    const header = ["Date", ...bySeries.map((s) => `${s.name} (${unit})`)];
    const rows = dates.map((d) => [
      d,
      ...bySeries.map((s) => {
        const v = s.m.get(d);
        return v != null ? numberFormat(v) : "";
      }),
    ]);
    downloadCsv(header, rows);
  };

  if (!series.length) return <EmptyMsg text="No devices selected." />;
  if (!dates.length)
    return <EmptyMsg text="No readings in the selected range." />;

  return (
    <>
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="text-muted text-sm">
          {dates.length} day{dates.length > 1 ? "s" : ""} · {series.length}{" "}
          series
        </span>
        <button className="btn" onClick={exportCsv}>
          <i className="bi bi-download" /> Export CSV
        </button>
      </div>
      <div className="data-table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              {bySeries.map((s) => (
                <th key={s.name}>
                  {s.name}
                  <div className="text-muted" style={{ fontWeight: 400 }}>
                    {unit}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((d) => (
              <tr key={d}>
                <td className="fw-600" style={{ backgroundColor: "#e9ecef" }}>
                  {d}
                </td>
                {bySeries.map((s, idx) => {
                  const v = s.m.get(d);
                  return (
                    <td
                      key={s.name}
                      style={{ backgroundColor: getColumnBgColor(idx) }}
                    >
                      {v != null ? numberFormat(v) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const DataTable: React.FC<DataTableProps> = ({
  series,
  unit = "",
  numberFormat = defaultFormat,
  grouped,
  readingUnit = "",
  changeUnit = "",
  readingFormat = defaultFormat,
  changeFormat = defaultFormat,
  preExistingFormat = defaultFormat,
  isCrackGauge = false,
}) => {
  if (grouped) {
    if (isCrackGauge) {
      return (
        <CrackGaugeTable
          grouped={grouped}
          readingUnit={readingUnit}
          changeUnit={changeUnit}
          readingFormat={readingFormat}
          changeFormat={changeFormat}
          preExistingFormat={preExistingFormat}
        />
      );
    }
    return (
      <GroupedTable
        grouped={grouped}
        readingUnit={readingUnit}
        changeUnit={changeUnit}
        readingFormat={readingFormat}
        changeFormat={changeFormat}
        preExistingFormat={preExistingFormat}
      />
    );
  }
  return (
    <SimpleTable
      series={series ?? []}
      unit={unit}
      numberFormat={numberFormat}
    />
  );
};

export default DataTable;
